import * as THREE from 'three';
import {RoomPlannerProduct} from '../types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';

// Type definitions for cabinet user data
interface CabinetValues {
    type_name?: string;
    description?: string;
    cabinet_door_gap?: number;
}

interface CabinetUserData {
    values?: CabinetValues;
    isDoorOrDrawerOpen?: boolean;
    id?: string | number;
    isLShapedProduct?: boolean;
    isFloorProduct?: boolean;
    ignoreSnapping?: boolean;
    currentWall?: THREE.Mesh;
    currentDirection?: string;
}

export interface CabinetGroup extends THREE.Group<THREE.Object3DEventMap> {
    userData: CabinetUserData;
}

/**
 * Determines if a cabinet has doors based on its type name and description
 * @param {CabinetGroup} cabinet - The cabinet group to check
 * @return {boolean} true if the cabinet has doors, false otherwise
 */
export const hasDoors = (cabinet: CabinetGroup): boolean => {
    const values = cabinet.userData.values as RoomPlannerProduct;
    if (!values) return false;

    // this will cross check if the the field option for the product says 'no door faces', by default we use drawer faces
    const includeFaces =
        typeof values.include_drawer_faces !== 'undefined'
            ? Boolean(values.include_drawer_faces)
            : true;

    const isNonSupplyMaterial =
        values.ext_material?.name?.toLowerCase() === 'na';

    // track out 'noInteraction' on the variables in initial load
    if (values?.template_3d?.[0]?.attributes?.variables) {
        const variables = JSON.parse(
            values.template_3d[0].attributes.variables
        ) as TemplateVariable;

        return (
            !variables.noInteractions && includeFaces && !isNonSupplyMaterial
        );
    }

    return false;
};

/**
 * Gets the cabinet door gap value if available
 * @param {CabinetGroup} cabinet - The cabinet group to check
 * @return {number | undefined} the door gap value or undefined
 */
export const getCabinetDoorGap = (
    cabinet: CabinetGroup
): number | undefined => {
    const values = cabinet.userData.values;
    return values ? values.cabinet_door_gap : undefined;
};

/**
 * Checks if a cabinet's door/drawer is currently open
 * @param {CabinetGroup} cabinet - The cabinet group to check
 * @return {boolean} true if door/drawer is open, false otherwise
 */
export const isDoorOrDrawerOpen = (cabinet: CabinetGroup): boolean => {
    return Boolean(cabinet.userData.isDoorOrDrawerOpen);
};

/**
 * Sets the door/drawer open state for a cabinet
 * @param {CabinetGroup} cabinet - The cabinet group to update
 * @param {boolean} isOpen - Whether the door/drawer should be open
 * @return {void}
 */
export const setDoorOrDrawerOpen = (
    cabinet: CabinetGroup,
    isOpen: boolean
): void => {
    cabinet.userData.isDoorOrDrawerOpen = isOpen;
};
